import { useState, useEffect } from "react";
import { Layout, Button, Tooltip } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { ReactComponent as PlatformIcon } from "./icons/PlatformIcon.svg";
import { ReactComponent as DemoFilesIcon } from "./icons/DemoFilesIcon.svg";
import { ReactComponent as ExtensionIcon } from "./icons/ExtensionIcon.svg";
import { ReactComponent as KnowledgeBaseIcon } from "./icons/KnowledgeBaseIcon.svg";
import { ReactComponent as LogoutIcon } from "./icons/LogoutIcon.svg";
import { observer } from "mobx-react-lite";
import { useMst } from "../../../stores/main";
import { BLINGS_API, getENV, HELP_CENTER_URL } from "../../../stores/consts";
import { ReactComponent as LogoIcon } from "../../../assets/logo-icon.svg";
import { ReactComponent as BlingsLogoFull } from "../../../assets/blings-logo-full.svg";
import "./GallerySidebar.scss";

const { Sider } = Layout;

const STORAGE_KEY = "gallery-sidebar-collapsed";
const demoDownloadUrl =
  "https://assets.blings.io/demo/OnBoardingVideoCollect.zip";
const extensionDownloadUrl = `${
  BLINGS_API[getENV()]
}/extension/latest/download`;

type SidebarItem = {
  key: string;
  label: string;
  icon: JSX.Element;
  onClick: () => void;
};

export const GallerySidebar = observer(() => {
  const {
    platformStore: { logout },
  } = useMst();

  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : false;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
  }, [collapsed]);

  const handleDownload = (url: string) => {
    const link = document.createElement("a");
    link.download = "demo";
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    link.remove();
  };

  const PLATFORM = `https://${
    getENV() === "dev" ? "dev." : ""
  }platform.blings.io/`;

  const mainItems: SidebarItem[] = [
    {
      key: "platform",
      label: "Platform",
      icon: <PlatformIcon className="sidebar-nav-icon" />,
      onClick: () => window.open(PLATFORM, "_blank"),
    },
  ];

  const resourceItems: SidebarItem[] = [
    {
      key: "demo-files",
      label: "Demo Files",
      icon: <DemoFilesIcon className="sidebar-nav-icon" />,
      onClick: () => handleDownload(demoDownloadUrl),
    },
    {
      key: "extension",
      label: "Extension",
      icon: <ExtensionIcon className="sidebar-nav-icon" />,
      onClick: () => handleDownload(extensionDownloadUrl),
    },
    {
      key: "knowledge-base",
      label: "Knowledge Base",
      icon: <KnowledgeBaseIcon className="sidebar-nav-icon" />,
      onClick: () => window.open(HELP_CENTER_URL, "_blank"),
    },
  ];

  const renderNavItems = (items: SidebarItem[]) =>
    items.map((item) => {
      const button = (
        <button
          key={item.key}
          type="button"
          className="sidebar-nav-item"
          onClick={item.onClick}
        >
          {item.icon}
          {!collapsed && <span className="sidebar-nav-text">{item.label}</span>}
        </button>
      );

      if (!collapsed) {
        return button;
      }

      return (
        <Tooltip key={item.key} title={item.label} placement="right">
          {button}
        </Tooltip>
      );
    });

  return (
    <Sider
      className="gallery-sidebar"
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      trigger={null}
      width={220}
      collapsedWidth={64}
      style={{
        backgroundColor: "var(--card)",
        borderRight: "1px solid var(--border)",
      }}
    >
      <div className="sidebar-header">
        <div className="sidebar-logo">
          {collapsed ? (
            <LogoIcon className="logo-icon" />
          ) : (
            <BlingsLogoFull className="logo-full" />
          )}
        </div>
        <Button
          type="text"
          className="collapse-btn"
          onClick={() => setCollapsed(!collapsed)}
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        />
      </div>

      <div className="sidebar-content">
        <nav className="sidebar-nav" aria-label="Main">
          {renderNavItems(mainItems)}
        </nav>

        <div className="sidebar-section-label">
          {!collapsed && <span>Resources</span>}
        </div>
        <nav className="sidebar-nav" aria-label="Resources">
          {renderNavItems(resourceItems)}
        </nav>
      </div>

      <div className="sidebar-footer">
        <Tooltip title={collapsed ? "Log out" : ""} placement="right">
          <button
            type="button"
            className="logout-section"
            onClick={() => logout()}
          >
            <LogoutIcon className="logout-icon" />
            {!collapsed && <span className="logout-text">Log out</span>}
          </button>
        </Tooltip>
      </div>
    </Sider>
  );
});
